import { useState } from 'react';
import { Modal, Button, Input, Textarea, Select, Stars, Toast } from '../ui/UI';
import AddressAutocomplete from '../ui/AddressAutocomplete';
import { addRestaurant, findDuplicate, addReview, setUserStatut } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';

const CUISINES = ['Italien', 'Japonais', 'Français', 'Mexicain', 'Indien', 'Chinois', 'Américain', 'Grec', 'Thaïlandais', 'Libanais', 'Espagnol', 'Autre'];
const PRIX = [
  { value: 1, label: '€ — Moins de 15€' },
  { value: 2, label: '€€ — 15€ à 30€' },
  { value: 3, label: '€€€ — 30€ à 60€' },
  { value: 4, label: '€€€€ — Plus de 60€' },
];

const FORM_INITIAL = {
  nom: '', adresse: '', type_cuisine: 'Autre',
  statut: 'teste', niveau_prix: 2,
  note: 0, commentaire: '', date_visite: '',
  lat: null, lng: null,
};

export default function AddRestaurantModal({ open, onClose, onAdded, defaultStatut = 'teste' }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [duplicate, setDuplicate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({ ...FORM_INITIAL, statut: defaultStatut });
  const [errors, setErrors] = useState({});

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => ({ ...e, [field]: undefined }));
  }

  // Appelé quand l'utilisateur sélectionne une suggestion d'adresse
  function handleAddressSelect(suggestion) {
    setForm(f => ({ ...f, adresse: suggestion.label, lat: suggestion.lat, lng: suggestion.lng }));
  }

  function validate() {
    const e = {};
    if (!form.nom.trim()) e.nom = 'Nom obligatoire';
    if (!form.adresse.trim()) e.adresse = 'Adresse obligatoire';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleNext() {
    if (!validate()) return;
    setLoading(true);
    try {
      const dup = await findDuplicate(form.nom, form.adresse);
      if (dup) { setDuplicate(dup); setStep(2); }
      else { await soumettre(); }
    } finally {
      setLoading(false);
    }
  }

  async function soumettre() {
    setLoading(true);
    try {
      // Si les coords ne sont pas encore set (adresse tapée manuellement sans sélection)
      let lat = form.lat;
      let lng = form.lng;
      if (!lat || !lng) {
        const { geocodeAdresse } = await import('../../lib/api');
        const coords = await geocodeAdresse(form.adresse);
        lat = coords?.lat || null;
        lng = coords?.lng || null;
      }

      const data = {
        nom: form.nom.trim(),
        adresse: form.adresse.trim(),
        type_cuisine: form.type_cuisine,
        niveau_prix: parseInt(form.niveau_prix),
        date_visite: form.date_visite || null,
        lat, lng,
        added_by: user?.id,
        note_moyenne: null,
        nb_avis: 0,
      };

      const restaurant = await addRestaurant(data);

      // Définir le statut PERSONNEL de l'utilisateur
      await setUserStatut(restaurant.id, user.id, form.statut);

      // Ajouter l'avis si noté
      if (form.note > 0) {
        await addReview(restaurant.id, user.id, form.note, form.commentaire);
      }

      setToast({ message: 'Restaurant ajouté ! 🎉', type: 'success' });
      onAdded?.(restaurant);
      handleClose();
    } catch (err) {
      setToast({ message: 'Erreur : ' + err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  async function handleMerge() {
    // Ajouter simplement l'avis + statut personnel sur le restaurant existant
    if (user) {
      await setUserStatut(duplicate.id, user.id, form.statut);
      if (form.note > 0) {
        await addReview(duplicate.id, user.id, form.note, form.commentaire);
      }
    }
    setToast({ message: 'Avis ajouté au restaurant existant !', type: 'success' });
    onAdded?.(duplicate);
    handleClose();
  }

  function handleClose() {
    setForm({ ...FORM_INITIAL, statut: defaultStatut });
    setErrors({});
    setStep(1);
    setDuplicate(null);
    onClose();
  }

  return (
    <>
      <Modal open={open} onClose={handleClose} title={step === 1 ? '🍽️ Ajouter un restaurant' : '⚠️ Restaurant existant'} width="560px">

        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <Input
              label="Nom du restaurant *"
              value={form.nom}
              onChange={e => update('nom', e.target.value)}
              placeholder="Ex : Le Petit Bistrot"
              error={errors.nom}
              icon="🍽️"
            />

            {/* Adresse avec autocomplétion */}
            <AddressAutocomplete
              value={form.adresse}
              onChange={val => update('adresse', val)}
              onSelect={handleAddressSelect}
              error={errors.adresse}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Select
                label="Type de cuisine"
                value={form.type_cuisine}
                onChange={e => update('type_cuisine', e.target.value)}
                options={CUISINES.map(c => ({ value: c, label: c }))}
              />
              <Select
                label="Niveau de prix"
                value={form.niveau_prix}
                onChange={e => update('niveau_prix', e.target.value)}
                options={PRIX.map(p => ({ value: p.value, label: p.label }))}
              />
            </div>

            {/* Statut PERSONNEL */}
            <div>
              <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 8 }}>
                Mon statut personnel
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { v: 'teste', icon: '✅', label: 'Je l\'ai testé' },
                  { v: 'tester', icon: '📌', label: 'Je veux le tester' },
                ].map(s => (
                  <button
                    key={s.v}
                    type="button"
                    onClick={() => update('statut', s.v)}
                    style={{
                      flex: 1, padding: '10px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                      border: `2px solid ${form.statut === s.v ? 'var(--brand)' : 'var(--border)'}`,
                      background: form.statut === s.v ? 'var(--brand-pale)' : 'var(--surface)',
                      color: form.statut === s.v ? 'var(--brand)' : 'var(--text-2)',
                      cursor: 'pointer', transition: 'all 0.2s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    {s.icon} {s.label}
                  </button>
                ))}
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>
                Ce statut est uniquement visible pour vous. Les autres utilisateurs auront leur propre statut.
              </p>
            </div>

            {form.statut === 'teste' && (
              <>
                <Input
                  label="Date de visite (optionnel)"
                  type="date"
                  value={form.date_visite}
                  onChange={e => update('date_visite', e.target.value)}
                />
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 8 }}>
                    Ma note
                  </label>
                  <Stars note={form.note} size={32} interactive onChange={v => update('note', v)} />
                </div>
                {form.note > 0 && (
                  <Textarea
                    label="Mon avis (optionnel)"
                    value={form.commentaire}
                    onChange={e => update('commentaire', e.target.value)}
                    placeholder="Partagez votre expérience…"
                  />
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
              <Button variant="outline" onClick={handleClose}>Annuler</Button>
              <Button onClick={handleNext} loading={loading}>Continuer</Button>
            </div>
          </div>
        )}

        {step === 2 && duplicate && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🤔</div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Ce restaurant existe déjà !</h3>
            <p style={{ color: 'var(--text-2)', marginBottom: 6 }}>
              «&nbsp;<strong>{duplicate.nom}</strong>&nbsp;» est déjà dans la base.
            </p>
            <p style={{ color: 'var(--text-3)', fontSize: 14, marginBottom: 28 }}>
              Voulez-vous ajouter votre avis à ce restaurant, ou créer un doublon ?
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="outline" onClick={() => setStep(1)}>← Modifier</Button>
              <Button variant="secondary" onClick={soumettre} loading={loading}>Créer quand même</Button>
              <Button onClick={handleMerge} loading={loading}>Ajouter mon avis ✅</Button>
            </div>
          </div>
        )}
      </Modal>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}
